import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ContentCopy as CopyIcon } from "@mui/icons-material";
import {
  useSystemSsoConfig,
  useUpdateSystemSsoConfig,
  useSystemSsoPortalAssignments,
  useUpdateSystemSsoPortalAssignments,
} from "./hooks/useSystemSso";

const FIELD_WIDTH = 360;
const LABEL_WIDTH = 220;

function buildRedirectUri(): string {
  let base = import.meta.env.VITE_API_BASE_URL || "/api/v1/";
  if (base.startsWith("/")) {
    base = window.location.origin + base;
  }
  if (!base.endsWith("/")) base += "/";
  return `${base}user-service/auth/system-sso/callback`;
}

interface FieldRowProps {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
  align?: "center" | "flex-start";
}

const FieldRow: React.FC<FieldRowProps> = ({
  label,
  hint,
  required,
  children,
  align = "center",
}) => (
  <Stack direction="row" alignItems={align} spacing={2} sx={{ minHeight: 40 }}>
    <Box
      sx={{
        width: LABEL_WIDTH,
        flexShrink: 0,
        pt: align === "flex-start" ? 1 : 0,
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontSize: "0.875rem", color: "text.primary" }}
      >
        {label}
        {required && (
          <Box component="span" sx={{ color: "error.main", ml: 0.25 }}>
            *
          </Box>
        )}
      </Typography>
      {hint && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.7rem", display: "block", lineHeight: 1.3 }}
        >
          {hint}
        </Typography>
      )}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Stack>
);

type SubTab = "config" | "portals";

interface ConfigForm {
  client_id: string;
  client_secret: string;
  tenant_id: string;
  scopes: string;
  redirect_uri: string;
  allowed_email_domains: string;
  email_claim: string;
  auto_create_users: boolean;
  is_enabled: boolean;
}

const emptyConfig: ConfigForm = {
  client_id: "",
  client_secret: "",
  tenant_id: "",
  scopes: "openid email profile",
  redirect_uri: buildRedirectUri(),
  allowed_email_domains: "",
  email_claim: "email",
  auto_create_users: false,
  is_enabled: false,
};

function configToForm(config: { client_id: string; tenant_id: string; scopes: string; redirect_uri: string; allowed_email_domains: string | null; email_claim: string; auto_create_users: boolean; is_enabled: boolean } | null | undefined): ConfigForm {
  if (!config) return emptyConfig;
  return {
    client_id: config.client_id,
    client_secret: "",
    tenant_id: config.tenant_id,
    scopes: config.scopes,
    redirect_uri: config.redirect_uri || buildRedirectUri(),
    allowed_email_domains: config.allowed_email_domains ?? "",
    email_claim: config.email_claim || "email",
    auto_create_users: config.auto_create_users,
    is_enabled: config.is_enabled,
  };
}

const SsoSettingsForm: React.FC = () => {
  const { data: ssoConfig, isLoading: isLoadingConfig } =
    useSystemSsoConfig();
  const updateConfig = useUpdateSystemSsoConfig();
  const {
    data: assignmentsData,
    isLoading: isLoadingAssignments,
  } = useSystemSsoPortalAssignments();
  const updateAssignments = useUpdateSystemSsoPortalAssignments();

  const isEdit = Boolean(ssoConfig?.id);

  const [subTab, setSubTab] = useState<SubTab>("config");
  const [form, setForm] = useState<ConfigForm>(() => configToForm(ssoConfig));
  const [hydratedId, setHydratedId] = useState<string | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [configSuccess, setConfigSuccess] = useState(false);
  const [copied, setCopied] = useState(false);
  const [assignmentChanges, setAssignmentChanges] = useState<
    Record<string, boolean>
  >({});
  const [assignmentError, setAssignmentError] = useState<string | null>(null);
  const [assignmentSuccess, setAssignmentSuccess] = useState(false);

  // Hydrate form when config loads for the first time or changes
  if (ssoConfig && hydratedId !== ssoConfig.id) {
    setHydratedId(ssoConfig.id);
    setForm(configToForm(ssoConfig));
  }

  const isConfigValid =
    form.client_id.trim() !== "" &&
    form.tenant_id.trim() !== "" &&
    (isEdit || form.client_secret.trim() !== "");

  const handleSaveConfig = () => {
    setConfigError(null);
    setConfigSuccess(false);
    updateConfig.mutate(
      {
        client_id: form.client_id,
        ...(form.client_secret ? { client_secret: form.client_secret } : {}),
        tenant_id: form.tenant_id,
        scopes: form.scopes || "openid email profile",
        redirect_uri: form.redirect_uri,
        allowed_email_domains: form.allowed_email_domains || null,
        email_claim: form.email_claim || "email",
        auto_create_users: form.auto_create_users,
        is_enabled: form.is_enabled,
      },
      {
        onSuccess: () => setConfigSuccess(true),
        onError: (err: unknown) => {
          const msg =
            (err as { response?: { data?: { detail?: string } } })?.response
              ?.data?.detail ||
            (err as { message?: string })?.message ||
            "Failed to save SSO configuration";
          setConfigError(msg);
        },
      }
    );
  };

  // Portal assignments
  const assignments = useMemo(
    () => assignmentsData?.assignments ?? [],
    [assignmentsData]
  );
  const hasAssignmentChanges = Object.keys(assignmentChanges).length > 0;

  const effectiveAssignments = useMemo(
    () =>
      assignments.map((a) => ({
        ...a,
        sso_enabled:
          assignmentChanges[a.portal_id] ?? a.sso_enabled,
      })),
    [assignments, assignmentChanges]
  );

  const handleTogglePortal = (portalId: string, enabled: boolean) => {
    const original = assignments.find((a) => a.portal_id === portalId);
    if (original && original.sso_enabled === enabled) {
      setAssignmentChanges((prev) => {
        const next = { ...prev };
        delete next[portalId];
        return next;
      });
    } else {
      setAssignmentChanges((prev) => ({ ...prev, [portalId]: enabled }));
    }
  };

  const handleSaveAssignments = () => {
    setAssignmentError(null);
    setAssignmentSuccess(false);
    const updates = Object.entries(assignmentChanges).map(
      ([portal_id, is_enabled]) => ({ portal_id, is_enabled })
    );
    updateAssignments.mutate(updates, {
      onSuccess: () => {
        setAssignmentChanges({});
        setAssignmentSuccess(true);
      },
      onError: (err: unknown) => {
        const msg =
          (err as { response?: { data?: { detail?: string } } })?.response
            ?.data?.detail ||
          (err as { message?: string })?.message ||
          "Failed to update portal assignments";
        setAssignmentError(msg);
      },
    });
  };

  const compactField = {
    size: "small" as const,
    fullWidth: true,
    sx: { maxWidth: FIELD_WIDTH },
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Tabs
          value={subTab}
          onChange={(_, val) => {
            setSubTab(val as SubTab);
            setConfigSuccess(false);
            setAssignmentSuccess(false);
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="config" label="Configuration" />
          <Tab value="portals" label="Portal Assignments" />
        </Tabs>
      </Box>

      {subTab === "config" && (
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Configure Microsoft Entra ID (Azure AD) single sign-on for platform
            and portal admin login.
          </Typography>

          {isLoadingConfig && (
            <Stack direction="row" justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress />
            </Stack>
          )}

          {!isLoadingConfig && (
            <>
              {configError && (
                <Alert
                  severity="error"
                  onClose={() => setConfigError(null)}
                  sx={{ mb: 1 }}
                >
                  {configError}
                </Alert>
              )}
              {configSuccess && (
                <Alert
                  severity="success"
                  onClose={() => setConfigSuccess(false)}
                  sx={{ mb: 1 }}
                >
                  SSO configuration saved successfully.
                </Alert>
              )}

              <FieldRow label="Client ID" required>
                <TextField
                  {...compactField}
                  value={form.client_id}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, client_id: e.target.value }))
                  }
                  placeholder="Application (client) ID from Azure"
                />
              </FieldRow>

              <FieldRow
                label="Client Secret"
                hint={
                  isEdit ? "Leave blank to keep current secret" : undefined
                }
                required={!isEdit}
              >
                <TextField
                  {...compactField}
                  type="password"
                  value={form.client_secret}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      client_secret: e.target.value,
                    }))
                  }
                />
              </FieldRow>

              <FieldRow
                label="Tenant ID"
                hint="Azure AD tenant UUID, 'common', or 'organizations'"
                required
              >
                <TextField
                  {...compactField}
                  value={form.tenant_id}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, tenant_id: e.target.value }))
                  }
                />
              </FieldRow>

              <FieldRow label="Scopes" hint="Space-separated OAuth scopes">
                <TextField
                  {...compactField}
                  value={form.scopes}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, scopes: e.target.value }))
                  }
                />
              </FieldRow>

              <FieldRow
                label="Redirect URI"
                hint="Copy this to your Azure app registration"
                align="flex-start"
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    bgcolor: "action.hover",
                    borderRadius: 1,
                    maxWidth: FIELD_WIDTH,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                        wordBreak: "break-all",
                      }}
                    >
                      {form.redirect_uri}
                    </Typography>
                  </Box>
                  <Tooltip title={copied ? "Copied!" : "Copy"}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(form.redirect_uri);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </FieldRow>

              <FieldRow
                label="Allowed Email Domains"
                hint="Comma-separated. Empty = allow all."
              >
                <TextField
                  {...compactField}
                  value={form.allowed_email_domains}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      allowed_email_domains: e.target.value,
                    }))
                  }
                  placeholder="e.g. company.com, subsidiary.com"
                />
              </FieldRow>

              <FieldRow
                label="Email Claim"
                hint="ID token claim used to identify the user (e.g. email, preferred_username, upn)"
              >
                <TextField
                  {...compactField}
                  value={form.email_claim}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      email_claim: e.target.value,
                    }))
                  }
                  placeholder="email"
                />
              </FieldRow>

              <FieldRow
                label="Auto-create users"
                hint="Create admin accounts on first SSO login (created as inactive, admin must activate)"
              >
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={form.auto_create_users}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          auto_create_users: e.target.checked,
                        }))
                      }
                    />
                  }
                  label=""
                />
              </FieldRow>

              <FieldRow label="Enabled">
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={form.is_enabled}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          is_enabled: e.target.checked,
                        }))
                      }
                    />
                  }
                  label=""
                />
              </FieldRow>

              <Stack direction="row" justifyContent="flex-end" sx={{ pt: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveConfig}
                  disabled={!isConfigValid || updateConfig.isPending}
                  startIcon={
                    updateConfig.isPending ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null
                  }
                >
                  {isEdit ? "Save Configuration" : "Create Configuration"}
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      )}

      {subTab === "portals" && (
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Control which portals inherit the system Microsoft SSO for portal
            admin login.
          </Typography>

          {isLoadingAssignments && (
            <Stack direction="row" justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress />
            </Stack>
          )}

          {!isLoadingAssignments && assignments.length === 0 && (
            <Alert severity="info">
              No portals found. Create a portal first to assign SSO.
            </Alert>
          )}

          {!isLoadingAssignments && assignments.length > 0 && (
            <>
              {assignmentError && (
                <Alert
                  severity="error"
                  onClose={() => setAssignmentError(null)}
                  sx={{ mb: 1 }}
                >
                  {assignmentError}
                </Alert>
              )}
              {assignmentSuccess && (
                <Alert
                  severity="success"
                  onClose={() => setAssignmentSuccess(false)}
                  sx={{ mb: 1 }}
                >
                  Portal assignments updated successfully.
                </Alert>
              )}

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Portal Name</TableCell>
                      <TableCell>Slug</TableCell>
                      <TableCell align="center">SSO Enabled</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {effectiveAssignments.map((a) => (
                      <TableRow key={a.portal_id}>
                        <TableCell>{a.portal_name}</TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                          >
                            {a.portal_slug}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Switch
                            size="small"
                            checked={a.sso_enabled}
                            onChange={(e) =>
                              handleTogglePortal(
                                a.portal_id,
                                e.target.checked
                              )
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Stack direction="row" justifyContent="flex-end" sx={{ pt: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveAssignments}
                  disabled={
                    !hasAssignmentChanges || updateAssignments.isPending
                  }
                  startIcon={
                    updateAssignments.isPending ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null
                  }
                >
                  Save Assignments
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default SsoSettingsForm;
